import { useCallback, useRef, useState } from "react";

import { ProjectRow } from "../../../../../models";
import { generateProjectImportTemplate } from "../../../../../service/project";
import { initiateDocumentDownloadViaBrowser, ProjectReferenceOptions } from "../../../../../utils";
import { MissingColumnsError, parseProjectWorkbook } from "../utils";

interface UseBulkUploadModalParams {
  options: ProjectReferenceOptions;
  /** The parsed rows, once a file has been read. The modal closes itself after this. */
  onParsed: (rows: ProjectRow[]) => void;
  /** Runs as the modal opens - the step uses it to drop errors and a validation about the old file. */
  onOpen?: () => void;
}

export interface UseBulkUploadModalReturnData {
  showUploadModal: boolean;
  isParsing: boolean;
  parseError: string | undefined;
  showOutOfDateTemplateHint: boolean;
  handleOpenUploadModal: () => void;
  handleCancelUpload: () => void;
  handleFileSelected: (file: File | null) => void;
  handleUpload: () => void;
  handleDownloadTemplate: () => void;
}

/**
 * Everything the BulkUploadModal needs to pick, parse and hand back a project workbook: the chosen
 * file, the parse in flight, and the messages for a file it could not read. Owns nothing about the
 * rows themselves - those go to the caller through onParsed.
 */
export const useBulkUploadModal = ({
  options,
  onParsed,
  onOpen,
}: UseBulkUploadModalParams): UseBulkUploadModalReturnData => {
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isParsing, setIsParsing] = useState(false);
  const [parseError, setParseError] = useState<string | undefined>(undefined);
  const [showOutOfDateTemplateHint, setShowOutOfDateTemplateHint] = useState(false);
  // Closing the modal or picking another file must abandon a parse already in flight - the modal's
  // close icon and backdrop stay live while it runs.
  const parseRequestIdRef = useRef(0);

  const abandonParse = useCallback(() => {
    parseRequestIdRef.current += 1;
    setIsParsing(false);
  }, []);

  const resetMessages = useCallback(() => {
    setParseError(undefined);
    setShowOutOfDateTemplateHint(false);
  }, []);

  const handleDownloadTemplate = useCallback(() => {
    generateProjectImportTemplate()
      .then((response) => initiateDocumentDownloadViaBrowser(response))
      .catch(() => setParseError("Couldn't download the template - please try again later."));
  }, []);

  const handleFileSelected = useCallback(
    (file: File | null) => {
      abandonParse();
      setSelectedFile(file);
      resetMessages();
    },
    [abandonParse, resetMessages]
  );

  const handleCancelUpload = useCallback(() => {
    abandonParse();
    setShowUploadModal(false);
    setSelectedFile(null);
    resetMessages();
  }, [abandonParse, resetMessages]);

  // Used by both the card's Upload file button and the table's Replace file button.
  const handleOpenUploadModal = useCallback(() => {
    abandonParse();
    onOpen?.();
    setSelectedFile(null);
    resetMessages();
    setShowUploadModal(true);
  }, [abandonParse, onOpen, resetMessages]);

  const handleUpload = useCallback(() => {
    if (!selectedFile) return;

    // The template carries the uuids in its hidden columns, so a file built from it parses without
    // the reference data. It is only the fallback for a row whose uuid cell never recalculated, and
    // losing that would turn a readable file into a page of unrecognised values.
    if (options.hasLoadFailed) {
      setParseError("We couldn't load the reference data this file is checked against. Try again first.");
      return;
    }

    parseRequestIdRef.current += 1;
    const parseRequestId = parseRequestIdRef.current;

    setIsParsing(true);
    resetMessages();

    parseProjectWorkbook(selectedFile, {
      standardOptions: options.standardOptions,
      projectTypeOptions: options.projectTypeOptions,
      verifierOptions: options.verifierOptions,
      countryOptions: options.countryOptions,
    })
      .then((parsedRows) => {
        if (parseRequestId !== parseRequestIdRef.current) return;

        if (parsedRows.length === 0) {
          setParseError("That file has no projects in it. Check you are using the latest template.");
          setShowOutOfDateTemplateHint(true);
          return;
        }

        onParsed(parsedRows);
        setShowUploadModal(false);
        setSelectedFile(null);
      })
      .catch((error) => {
        if (parseRequestId !== parseRequestIdRef.current) return;

        if (error instanceof MissingColumnsError) {
          setParseError(`Your file is missing these columns: ${error.columns.join(", ")}.`);
          setShowOutOfDateTemplateHint(true);
          return;
        }
        setParseError("We couldn't read that file. Upload an .xlsx built from the template.");
      })
      .finally(() => {
        if (parseRequestId !== parseRequestIdRef.current) return;
        setIsParsing(false);
      });
  }, [onParsed, options, resetMessages, selectedFile]);

  return {
    showUploadModal,
    isParsing,
    parseError,
    showOutOfDateTemplateHint,
    handleOpenUploadModal,
    handleCancelUpload,
    handleFileSelected,
    handleUpload,
    handleDownloadTemplate,
  };
};
